import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Pagination,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { alpha } from "@mui/material/styles";
import { Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";

import PublicIcon from "@mui/icons-material/Public";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import BoltIcon from "@mui/icons-material/Bolt";
import InsightsIcon from "@mui/icons-material/Insights";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import {
  crawlWebSource,
  crawlBatchWebSources,
  getCrawlTasks,
  deleteTask,
  killWebTask,
} from "../../services/web-services";
import { getAllResultsCount } from "../../services/generic_services";
import PopupForm from "../PopupForm";
import StatisticsPopup from "../StatisticsPopup";
import KpiTile from "../results/KpiTile";
import renderStatus from "../common/StatusChip";
import getEnv from "../../config";
import { createSocket } from "../../services/socket";

const baseURL = getEnv("REACT_APP_API_URL");
const WEB_COLOR = "#4FB6BC";

const CrawlSchema = Yup.object().shape({
  urls: Yup.string()
    .required("Required")
    .test(
      "valid-urls",
      "Each line must be a valid URL starting with http:// or https://",
      (value) => {
        if (!value) return false;
        const lines = value.split("\n").map((l) => l.trim()).filter(Boolean);
        return lines.length > 0 && lines.every((l) => /^https?:\/\/.+/.test(l));
      }
    ),
});

const SectionHeader = ({ title, action }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1.5 }}>
    <Typography variant="h6">{title}</Typography>
    {action}
  </Box>
);

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ letterSpacing: "0.08em", display: "block", mb: 1 }}
  >
    {children}
  </Typography>
);

const TaskCard = ({ task, onRun, onStop, onSettings, onStats, onDelete }) => {
  const navigate = useNavigate();
  const isRunning = task.state === "RUNNING";
  const createdAt = task.created_at && task.created_at["$date"]
    ? moment(task.created_at["$date"]).format("YYYY-MM-DD HH:mm")
    : "";

  const goToResults = () =>
    navigate({
      pathname: "/web/results",
      search: `?${new URLSearchParams({ site_id: task.site_id }).toString()}`,
    });

  return (
    <Card
      sx={{
        p: 2,
        borderLeft: `3px solid ${WEB_COLOR}`,
        "&:hover": { boxShadow: `0 2px 12px ${alpha(WEB_COLOR, 0.15)}` },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ mb: 0.5, minWidth: 0 }}>
            <Tooltip title={task.url || ""}>
              <Typography
                variant="subtitle1"
                onClick={goToResults}
                noWrap
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  color: "text.primary",
                  "&:hover": { color: WEB_COLOR },
                }}
              >
                {task.url}
              </Typography>
            </Tooltip>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
            {renderStatus(task.state)}
            {task.monitoring && (
              <Chip
                size="small"
                icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                label={`every ${task.monitoring_interval || 1}d`}
                variant="outlined"
              />
            )}
            {task.focused && (
              <Chip
                size="small"
                icon={<BoltIcon sx={{ fontSize: 14 }} />}
                // No threshold shown: the web crawler does not apply one. The
                // Reddit and Mastodon pages still show theirs, which is honoured.
                label="focused"
                variant="outlined"
              />
            )}
            {task.evasive && (
              <Chip
                size="small"
                icon={<VisibilityOffIcon sx={{ fontSize: 14 }} />}
                label={`evasive${task.evasive_mode ? ` · ${task.evasive_mode}` : ""}`}
                variant="outlined"
              />
            )}
            <Typography variant="caption" color="text.secondary">
              depth {task.depth ?? 0}
            </Typography>
            {task.filterByDomain && (
              <Typography variant="caption" color="text.secondary">
                · same-domain
              </Typography>
            )}
            {createdAt && (
              <Typography variant="caption" color="text.disabled">
                · added {createdAt}
              </Typography>
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Run now">
            <span>
              <IconButton size="small" color="primary" onClick={() => onRun(task)} disabled={isRunning}>
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Stop">
            <span>
              <IconButton size="small" color="primary" onClick={() => onStop(task)} disabled={!isRunning}>
                <StopIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Statistics">
            <IconButton size="small" onClick={() => onStats(task)}>
              <InsightsIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Task settings">
            <IconButton size="small" onClick={() => onSettings(task)}>
              <SettingsIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete source and related results">
            <IconButton size="small" onClick={() => onDelete(task)}>
              <DeleteIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Card>
  );
};

const STATE_FILTERS = [
  { key: "ALL", label: "All", match: () => true },
  { key: "QUEUED", label: "Queued", match: (s) => s === "PENDING" || s === "QUEUED" },
  { key: "RUNNING", label: "Running", match: (s) => s === "RUNNING" },
  { key: "COMPLETED", label: "Completed", match: (s) => s === "SUCCESS" || s === "COMPLETED" },
  { key: "FAILED", label: "Failed", match: (s) => s === "FAILED" || s === "FAILURE" },
  { key: "KILLED", label: "Killed", match: (s) => s === "KILLED" || s === "REVOKED" || s === "CANCELED" },
];

const PAGE_SIZE = 10;

const TaskPage = () => {
  const [tasks, setTasks] = useState([]);
  const [docsCount, setDocsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTask, setActiveTask] = useState();
  const [openPopupForm, setOpenPopupForm] = useState(false);
  const [statisticsPopupOpen, setStatisticsPopupOpen] = useState(false);
  const [stateFilter, setStateFilter] = useState("ALL");
  const [taskSearch, setTaskSearch] = useState("");
  const [page, setPage] = useState(1);

  const updateTaskParams = (data) => {
    if (!activeTask) return;
    setTasks((prev) =>
      prev.map((task) =>
        task.site_id === activeTask.site_id
          ? {
              ...task,
              filterByDomain: data.filterByDomain,
              depth: data.depth,
              monitoring: data.monitoring,
              monitoring_interval: data.monitoring_interval,
              focused: data.focused,
              threshold: data.threshold,
              evasive: data.evasive,
              evasive_mode: data.evasive_mode,
            }
          : task
      )
    );
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [taskRes, countRes] = await Promise.allSettled([
        getCrawlTasks(),
        getAllResultsCount(),
      ]);
      if (taskRes.status === "fulfilled") setTasks(taskRes.value || []);
      if (countRes.status === "fulfilled") setDocsCount(countRes.value?.documents || 0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Socket.IO for real-time state updates
  useEffect(() => {
    if (!baseURL) return;
    const socket = createSocket();
    socket.on("state_update", (msg) => {
      const { site_id, state } = msg;
      setTasks((prev) =>
        prev.map((task) => (task.site_id === site_id ? { ...task, state } : task))
      );
    });
    return () => socket.disconnect();
  }, []);

  const submitCrawlingTask = (values) => {
    // Re-run of existing task (has 'url') vs new submission (has 'urls')
    if (values.url && !values.urls) {
      try {
        crawlWebSource(values);
      } catch (error) {
        console.log("Error submitting task");
      }
      setTimeout(() => fetchAll(), 2000);
      return;
    }

    const urlList = (values.urls || "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    if (urlList.length === 0) return;

    try {
      if (urlList.length === 1) {
        crawlWebSource({ ...values, url: urlList[0] });
      } else {
        const mode = values.executionMode || "sequential";
        crawlBatchWebSources({
          ...values,
          urls: urlList,
          mode,
          concurrency: mode === "parallel" ? (values.concurrency || 2) : 1,
        });
      }
    } catch (error) {
      console.log("Error submitting task");
    }

    setTimeout(() => fetchAll(), 2000);
  };

  const killTask = async (task) => {
    try {
      await killWebTask(task);
    } catch (error) {
      console.log(error);
    }
  };

  const deleteTaskFromPage = async (task) => {
    try {
      await deleteTask(task);
      setTimeout(() => fetchAll(), 2000);
    } catch (error) {
      console.log("Error ", error);
    }
  };

  const openSettings = (task) => {
    setActiveTask(null);
    setActiveTask(task);
    setOpenPopupForm(true);
  };

  const openStats = (task) => {
    setActiveTask(task);
    setStatisticsPopupOpen(true);
  };

  const kpis = useMemo(() => {
    const total = tasks.length;
    const running = tasks.filter((t) => t.state === "RUNNING").length;
    const scheduled = tasks.filter((t) => t.monitoring).length;
    return { total, running, scheduled };
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    const matcher = (STATE_FILTERS.find((f) => f.key === stateFilter) || STATE_FILTERS[0]).match;
    const q = taskSearch.trim().toLowerCase();
    return tasks.filter((t) => {
      if (!matcher(t.state)) return false;
      if (q && !(t.url || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [tasks, stateFilter, taskSearch]);

  const paginatedTasks = useMemo(
    () => filteredTasks.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredTasks, page]
  );

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1.5 }}>
        <MuiLink component={RouterLink} to="/results" underline="hover" color="text.secondary">
          All Results
        </MuiLink>
        <Typography variant="body2" color="text.primary">Web</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Web Crawling
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Crawl dark and surface web sources for cybersecurity-relevant content
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup value="tasks" exclusive size="small">
            <ToggleButton component={RouterLink} to="/tasks" value="tasks">
              Tasks
            </ToggleButton>
            <ToggleButton component={RouterLink} to="/web/results" value="results">
              Results
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchAll} size="small" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* KPI strip */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiTile
            label="Sources"
            value={kpis.total}
            unit="tracked"
            accent={WEB_COLOR}
            icon={<PublicIcon fontSize="small" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiTile
            label="Running"
            value={kpis.running}
            unit="now"
            accent="#4caf50"
            icon={<BoltIcon fontSize="small" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiTile
            label="Scheduled"
            value={kpis.scheduled}
            unit="monitoring"
            accent="#ff9800"
            icon={<ScheduleIcon fontSize="small" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiTile
            label="Documents"
            value={docsCount}
            unit="collected"
            accent={WEB_COLOR}
            icon={<InsightsIcon fontSize="small" />}
            navigateTo="/web/results"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Form + Tasks */}
      <Grid container spacing={3}>
        {/* Add new source */}
        <Grid item xs={12} md={5} lg={4}>
          <Card sx={{ p: 2.5 }}>
            <SectionHeader title="New crawl" />
            <FormGroup>
              <Formik
                initialValues={{
                  urls: "",
                  depth: 0,
                  focused: true,
                  evasive: false,
                  filterByDomain: true,
                  monitoring: false,
                  threshold: 0.5,
                  evasive_mode: "heuristics",
                  monitoring_interval: 3,
                  executionMode: "sequential",
                  concurrency: 2,
                }}
                validationSchema={CrawlSchema}
                onSubmit={(values, { resetForm }) => {
                  submitCrawlingTask(values);
                  resetForm();
                }}
              >
                {({ handleChange, handleBlur, handleSubmit, values, errors, touched, isValid }) => {
                  const urlCount = values.urls.split("\n").filter((l) => l.trim()).length;
                  return (
                    <Stack spacing={2.5}>
                      <Box>
                        <SectionLabel>SOURCES</SectionLabel>
                        <TextField
                          label="URLs (one per line)"
                          placeholder={"https://example.com\nhttps://another-site.org"}
                          onChange={handleChange("urls")}
                          onBlur={handleBlur("urls")}
                          value={values.urls}
                          size="small"
                          fullWidth
                          multiline
                          minRows={2}
                          maxRows={6}
                          error={Boolean(errors.urls && touched.urls)}
                          helperText={touched.urls ? errors.urls : ""}
                        />

                        {urlCount > 1 && (
                          <Box sx={{ mt: 1.5 }}>
                            <ToggleButtonGroup
                              value={values.executionMode}
                              exclusive
                              size="small"
                              fullWidth
                              onChange={(_, newMode) => {
                                if (newMode !== null) {
                                  handleChange("executionMode")({ target: { value: newMode } });
                                }
                              }}
                            >
                              <ToggleButton value="sequential">Sequential</ToggleButton>
                              <ToggleButton value="parallel">Parallel</ToggleButton>
                            </ToggleButtonGroup>

                            {values.executionMode === "parallel" && (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1.5, px: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                  Parallel tasks
                                </Typography>
                                <Slider
                                  value={values.concurrency}
                                  onChange={(_, val) => handleChange("concurrency")({ target: { value: val } })}
                                  min={2}
                                  max={10}
                                  step={1}
                                  marks
                                  valueLabelDisplay="auto"
                                  size="small"
                                  sx={{ flex: 1 }}
                                />
                                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 16 }}>
                                  {values.concurrency}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>

                      <Box>
                        <SectionLabel>CRAWL</SectionLabel>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 130px",
                            gap: 1.5,
                            alignItems: "center",
                          }}
                        >
                          <Tooltip title="Filters out all results that belong to a different domain than the URL">
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={
                                <Switch
                                  onChange={handleChange("filterByDomain")}
                                  value={values.filterByDomain}
                                  checked={values.filterByDomain}
                                  size="small"
                                />
                              }
                              label="Same domain"
                            />
                          </Tooltip>
                          <Tooltip title="Maximum URL path depth to crawl. 0 = no limit">
                            <TextField
                              onChange={handleChange("depth")}
                              value={values.depth}
                              type="number"
                              label="Depth"
                              size="small"
                              InputProps={{ inputProps: { min: 0 } }}
                            />
                          </Tooltip>
                        </Box>
                      </Box>

                      <Box>
                        <SectionLabel>SCHEDULE</SectionLabel>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 130px",
                            gap: 1.5,
                            alignItems: "center",
                          }}
                        >
                          <Tooltip title="Recrawl the source in specified intervals">
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={
                                <Switch
                                  onChange={handleChange("monitoring")}
                                  value={values.monitoring}
                                  checked={values.monitoring}
                                  size="small"
                                />
                              }
                              label="Monitoring"
                            />
                          </Tooltip>
                          <Tooltip title="Monitoring interval in days">
                            <TextField
                              onChange={handleChange("monitoring_interval")}
                              value={values.monitoring_interval}
                              type="number"
                              label="Interval (d)"
                              size="small"
                              disabled={!values.monitoring}
                              InputProps={{ inputProps: { min: 0 } }}
                            />
                          </Tooltip>
                        </Box>
                      </Box>

                      <Box>
                        <SectionLabel>FILTERING</SectionLabel>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 130px",
                            gap: 1.5,
                            alignItems: "center",
                          }}
                        >
                          <Tooltip title="Only pages the classifier marks as relevant are kept and sent to MISP">
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={
                                <Switch
                                  onChange={handleChange("focused")}
                                  value={values.focused}
                                  checked={values.focused}
                                  size="small"
                                />
                              }
                              label="Focused"
                            />
                          </Tooltip>
                          {/* Threshold deliberately left out: the web crawler never
                              compares it. PopupForm.js carries the full reason. The
                              default still reaches the API through initialValues. */}
                        </Box>
                      </Box>

                      <Box>
                        <SectionLabel>EVASION</SectionLabel>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 130px",
                            gap: 1.5,
                            alignItems: "center",
                          }}
                        >
                          <Tooltip title="Simulates human-like browsing to avoid crawl-detection mechanisms">
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={
                                <Switch
                                  onChange={handleChange("evasive")}
                                  value={values.evasive}
                                  checked={values.evasive}
                                  size="small"
                                />
                              }
                              label="Evasive"
                            />
                          </Tooltip>
                          <FormControl size="small" disabled={!values.evasive}>
                            <InputLabel>Mode</InputLabel>
                            <Select
                              label="Mode"
                              value={values.evasive_mode}
                              onChange={handleChange("evasive_mode")}
                            >
                              <MenuItem value="heuristics">Heuristics</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>

                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!isValid}
                        startIcon={<PlayArrowIcon />}
                        fullWidth
                        sx={{ mt: 0.5 }}
                      >
                        Start crawl
                      </Button>
                    </Stack>
                  );
                }}
              </Formik>
            </FormGroup>
          </Card>
        </Grid>

        {/* Active tasks */}
        <Grid item xs={12} md={7} lg={8}>
          <SectionHeader title={`Active tasks${kpis.total ? ` (${kpis.total})` : ""}`} />

          {/* Filter bar */}
          {tasks.length > 0 && (
            <Card sx={{ p: 1.5, mb: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={stateFilter}
                  onChange={(_, v) => { if (v) { setStateFilter(v); setPage(1); } }}
                  sx={{ flexWrap: "wrap" }}
                >
                  {STATE_FILTERS.map((f) => (
                    <ToggleButton key={f.key} value={f.key}>{f.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <TextField
                  size="small"
                  placeholder="Search URL…"
                  value={taskSearch}
                  onChange={(e) => { setTaskSearch(e.target.value); setPage(1); }}
                  sx={{ flex: 1, minWidth: 180 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Card>
          )}

          {tasks.length === 0 ? (
            <Card sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">
                No web sources being tracked yet. Add one on the left to start.
              </Typography>
            </Card>
          ) : filteredTasks.length === 0 ? (
            <Card sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">
                No tasks match the current filters.
              </Typography>
            </Card>
          ) : (
            <>
              <Stack spacing={1.5}>
                {paginatedTasks.map((task) => (
                  <TaskCard
                    key={task.site_id}
                    task={task}
                    onRun={submitCrawlingTask}
                    onStop={killTask}
                    onSettings={openSettings}
                    onStats={openStats}
                    onDelete={deleteTaskFromPage}
                  />
                ))}
              </Stack>
              {filteredTasks.length > PAGE_SIZE && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Pagination
                    count={Math.ceil(filteredTasks.length / PAGE_SIZE)}
                    page={page}
                    color="primary"
                    size="small"
                    onChange={(_, p) => setPage(p)}
                  />
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>

      {activeTask && (
        <PopupForm
          key={activeTask.site_id}
          open={openPopupForm}
          close={() => setOpenPopupForm(false)}
          task={activeTask}
          updateTaskParams={updateTaskParams}
        />
      )}
      {activeTask && (
        <StatisticsPopup
          open={statisticsPopupOpen}
          close={() => setStatisticsPopupOpen(false)}
          task={activeTask}
        />
      )}
    </Box>
  );
};

export default TaskPage;
